<template>
  <div class="chart-widget" v-if="flat">
    <div>
      <div class="registered-users__filter d-flex">
        <v-button size="xs-small mr-1" :inactive="currTab !== 'week'" @click="currTab = 'week'">{{ $t('widgets.period.week') }}</v-button>
        <v-button size="xs-small mr-1" :inactive="currTab !== 'month'" @click="currTab = 'month'">{{ $t('widgets.period.month') }}</v-button>
        <v-button size="xs-small mr-1" :inactive="currTab !== 'year'" @click="currTab = 'year'">{{ $t('widgets.period.year') }}</v-button>
        <v-button size="xs-small mr-1" :inactive="currTab !== 'threeYears'" @click="currTab = 'threeYears'">{{ $t('widgets.period.threeYears') }}</v-button>
      </div>
    </div>
    <div class="py-1">
      <apex-chart type="area" :options="chartOptions" :series="series" v-if="!$apollo.queries.chart.loading"></apex-chart>
      <div v-if="$apollo.queries.chart.loading" :style="{ height: (chartHeight + (chartHeight * .06)) + 'px', borderRadius: '.5rem' }" class="d-flex align-center justify-center bg-grey-lighten"><spinner width="3" height="3"></spinner></div>
    </div>
    <div class="d-flex justify-space-between">
      <div class="text--xs-small text-color-silver" v-if="!$apollo.queries.chart.loading">{{ computedPrevDate }}</div>
      <div class="text--xs-small text-color-silver" v-else><PuSkeleton height="1.5rem" width="6rem" /></div>
      <div class="text--xs-small text-color-silver" v-if="!$apollo.queries.chart.loading">{{ computedCurrDate }}</div>
      <div class="text--xs-small text-color-silver" v-else><PuSkeleton height="1.5rem" width="6rem" /></div>
    </div>

    <div class="divider my-1"></div>

    <div class="d-flex justify-space-between align-center">
      <div class="text--small text-color-silver">{{ $t('widgets.Dashboard.ChartWidget.totalInPeriod') }}</div>
      <div class="text--small text--medium" v-if="!$apollo.queries.chart.loading">{{ total }}</div>
      <div class="text--small text--medium" v-else><PuSkeleton height="1.5rem" width="6rem" /></div>
    </div>
  </div>

  <box style="max-width: 50rem;" v-else>
    <template v-slot:controls v-if="controls">
      <grab-control @grabstart="(e) => $emit('grabstart', e)" @grabend="(e) => $emit('grabend', e)"></grab-control>
      <kebab-menu>
        <action-link icon-left="icon-settings" size="small" class="mb-1" @click="$emit('configure')" v-if="editable">{{ $t('widgets.Dashboard.configure') }}</action-link>
        <action-link icon-left="icon-trash" size="small" @click="$emit('delete')">{{ $t('widgets.Dashboard.remove') }}</action-link>
      </kebab-menu>
    </template>

    <template v-slot:title>
      <div class="stat-widget__title text-color-space text--bold">{{ $t('widgets.Dashboard.ChartWidget.title') }}</div>
    </template>

    <div class="chart-widget">
      <div>
        <div class="registered-users__filter d-flex">
          <v-button size="xs-small mr-1" :inactive="currTab !== 'week'" @click="currTab = 'week'">{{ $t('widgets.period.week') }}</v-button>
          <v-button size="xs-small mr-1" :inactive="currTab !== 'month'" @click="currTab = 'month'">{{ $t('widgets.period.month') }}</v-button>
          <v-button size="xs-small mr-1" :inactive="currTab !== 'year'" @click="currTab = 'year'">{{ $t('widgets.period.year') }}</v-button>
          <v-button size="xs-small mr-1" :inactive="currTab !== 'threeYears'" @click="currTab = 'threeYears'">{{ $t('widgets.period.threeYears') }}</v-button>
        </div>
      </div>
      <div class="py-1">
        <div v-if="$apollo.queries.chart.loading" :style="{ height: (chartHeight + (chartHeight * .06)) + 'px', borderRadius: '.5rem' }" class="d-flex align-center justify-center bg-grey-lighten"><spinner width="3" height="3"></spinner></div>
        <apex-chart type="area" :options="chartOptions" :series="series" v-else></apex-chart>
      </div>
      <div class="d-flex justify-space-between">
        <div class="text--xs-small text-color-silver" v-if="!$apollo.queries.chart.loading">{{ computedPrevDate }}</div>
        <div class="text--xs-small text-color-silver" v-else><PuSkeleton height="1.5rem" width="6rem" /></div>
        <div class="text--xs-small text-color-silver" v-if="!$apollo.queries.chart.loading">{{ computedCurrDate }}</div>
        <div class="text--xs-small text-color-silver" v-else><PuSkeleton height="1.5rem" width="6rem" /></div>
      </div>

      <div class="divider my-1"></div>

      <div class="d-flex justify-space-between align-center">
        <div class="text--small text-color-silver">{{ $t('widgets.Dashboard.ChartWidget.totalInPeriod') }}</div>
        <div class="text--small text--medium" v-if="!$apollo.queries.chart.loading">{{ total }}</div>
        <div class="text--small text--medium" v-else><PuSkeleton height="1.5rem" width="6rem" /></div>
      </div>
    </div>
  </box>
</template>

<script>
import Box from '@/ui/slots/Box'
import KebabMenu from '@/ui/controls/KebabMenu'
import ActionLink from '@/ui/controls/ActionLink'
import GrabControl from '@/ui/controls/GrabControl'

import vButton from '@/ui/controls/vButton'
import Spinner from '@/ui/elements/Spinner'

import ApexChart from 'vue-apexcharts'

import { ChartStatGQL } from '@/graphql/queries'

import currContractId from '@/mixins/currContractId'
import toSimpleDate from '@/mixins/toSimpleDate'
import toCurrency from '@/mixins/toCurrency'

import { now, weekAgo, monthAgo, yearAgo, threeYearsAgo } from '@/utils'
import toMonthDate from '@/mixins/toMonthDate'
import merchantCurrency from '@/mixins/merchantCurrency'

let currTab = 'year'

export default {
  components: {
    Box,
    KebabMenu,
    ActionLink,
    GrabControl,

    vButton,
    Spinner,

    ApexChart
  },

  mixins: [currContractId, toSimpleDate, toCurrency, toMonthDate, merchantCurrency],

  props: {
    controls: {
      type: Boolean,
      default: false
    },

    editable: {
      type: Boolean,
      default: false
    },

    /**
     * @values 'COMMISSION'
     */
    source: {
      type: String,
      default: 'COMMISSION'
    },

    flat: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      currTab: currTab,
      loading: true,
      chartHeight: 250
    }
  },

  watch: {
    currTab (val) {
      currTab = val
    }
  },

  apollo: {
    chart: {
      query: ChartStatGQL,
      variables () {
        return {
          params: {
            type: this.source,
            attribution: this.attribution,
            startDate: this.prevDate.toISOString(),
            endDate: this.currDate.toISOString()
            // partnerContractId: this.currContractId
          }
        }
      },
      skip () {
        return !this.currContractId
      },
      update: data => data.merchant.stats.chart
    }
  },

  computed: {
    attribution () {
      const attributions = {
        week: 'DAY',
        month: 'DAY',
        year: 'WEEK',
        threeYears: 'MONTH'
      }

      return attributions[this.currTab]
    },

    currDate () {
      return now
    },

    prevDate () {
      const dates = {
        week: weekAgo(),
        month: monthAgo(),
        year: yearAgo(),
        threeYears: threeYearsAgo()
      }

      return dates[this.currTab]
    },

    computedPrevDate () {
      if (!this.chartData.labels.length) {
        return '—'
      }

      return this.chartData.labels[0]
    },

    computedCurrDate () {
      if (!this.chartData.labels.length) {
        return '—'
      }

      return this.chartData.labels[this.chartData.labels.length - 1]
    },

    chartOptions () {
      return {
        chart: {
          type: 'area',
          height: this.chartHeight,
          // height: '100%',
          // width: '100%',
          animations: { enabled: false },
          zoom: {
            enabled: false
          },
          stacked: false,
          offsetX: -13,
          offsetY: 10,
          sparkline: {
            enabled: false
          },
          toolbar: {
            show: false
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth'
          // curve: 'straight'
        },
        labels: this.chartData.labels,
        xaxis: {
          type: 'string',
          labels: {
            rotateAlways: false,
            trim: true,
            show: false
          }
        },
        yaxis: {
          opposite: false,
          showAlways: false,
          labels: {
            align: 'left',
            formatter: (value) => {
              return this.toCurrency(value, this.merchantCurrency)
            }
          },
          decimalsInFloat: 0
        }
      }
    },

    series () {
      if (!this.chartData.data.length) {
        return [{
          name: 'Комиссия',
          data: []
        }]
      }
      return [{
        name: 'Комиссия',
        data: this.chartData.data
      }]
    },

    dayCommissionList () {
      if (!this.chart) {
        return []
      }

      return this.chart
    },

    chartData () {
      const out = {
        data: [],
        labels: []
      }

      const initialDate = new Date(this.currDate)
      const commissionList = this.dayCommissionList
      const attribution = this.attribution

      if (!commissionList.length) {
        return out
      }

      let currDate = new Date(this.currDate)
      const prevDate = new Date(this.prevDate)

      if (attribution === 'WEEK') {
        const untilMonday = initialDate.getDay() === 0 ? 1 : 8 - initialDate.getDay()

        if (initialDate.getDay() !== 1) {
          currDate = currDate.getTime() + (86400000 * untilMonday)
          currDate = new Date(currDate)
        }

        let lastVal
        let newDate = new Date(currDate)
        while (newDate > prevDate) {
          lastVal = newDate
          newDate = newDate - (7 * 86400000)
          out.data.push(0)
          out.labels.push(this.toSimpleDate(newDate, true) + ' — ' + this.toSimpleDate(lastVal, true))
        }

        out.labels = out.labels.reverse()

        for (let i = 0; i < commissionList.length; i++) {
          const data = commissionList[i].commission
          const label = this.toSimpleDate(this.dayCommissionList[i].startDate, true) + ' — ' + this.toSimpleDate(commissionList[i].endDate, true)
          const index = out.labels.findIndex(l => l === label)
          out.labels[index] = label
          out.data[index] = data
        }
      } else if (attribution === 'MONTH') {
        let newDate = new Date(currDate)
        out.data.push(0)
        out.labels.push(this.toMonthDate(newDate, true))
        while (newDate > prevDate) {
          newDate = new Date(newDate.setMonth(newDate.getMonth() - 1))
          out.data.push(0)
          out.labels.push(this.toMonthDate(newDate, true))
        }

        out.labels.reverse()

        for (let i = 0; i < commissionList.length; i++) {
          const data = commissionList[i].commission
          const label = this.toMonthDate(commissionList[i].startDate, true)
          const index = out.labels.findIndex(l => l === label)
          out.labels[index] = label
          out.data[index] = data
        }
      } else if (attribution === 'DAY') {
        let newDate = new Date(currDate)
        while (newDate >= prevDate) {
          out.data.push(0)
          out.labels.push(this.toSimpleDate(newDate, true))
          newDate = newDate - 86400000
        }

        out.data.push(0)
        out.labels.push(this.toSimpleDate(newDate, true))
        newDate = newDate - 86400000

        out.labels = out.labels.reverse()

        for (let i = 0; i < commissionList.length; i++) {
          const data = commissionList[i].commission
          const label = this.toSimpleDate(this.dayCommissionList[i].startDate, true)
          const index = out.labels.findIndex(l => l === label)
          out.labels[index] = label
          out.data[index] = data
        }
      }

      return out
    },

    total () {
      let total = 0
      this.chartData.data.forEach(i => {
        total += i
      })
      return this.toCurrency(total, this.merchantCurrency)
    }
  }
}
</script>
